import {
  fetchApi,
  useAction,
  useCreate,
  useDelete,
  useDetail,
  useList,
  usePartialUpdate,
  usePlainList,
  useUpdate
} from "@inmagik/react-crud"
import { normalizeParams } from "@inmagik/react-crud/dist/crud/utils"
import { useCallback } from "react"
import { useAuth } from "../auth"
import { API_URL } from "../constants"
import type { Resource, ResourceMedia } from "../types"

export function useResourcesList(params: Record<string, string> = {}) {
  return useList<Resource>(`${API_URL}/admin/resources`, {
    query: params,
  })
}

export function useResource(id: number | undefined) {
  return useDetail<Resource>(`${API_URL}/admin/resources`, id?.toString())
}

export function useCreateResource() {
  return useCreate<Resource>(`${API_URL}/admin/resources`, { encode: "formdata" })
}

export function useUpdateResource() {
  return useUpdate<Resource>(`${API_URL}/admin/resources`, { encode: "formdata" })
}

export function useDeleteResource() {
  return useDelete(`${API_URL}/admin/resources`)
}

export function usePartialUpdateResource() {
  return usePartialUpdate<Resource>(`${API_URL}/admin/resources`)
}

export function useDeleteMultiResource() {
  return useAction(`${API_URL}/admin/resources/delete_multi/`, {
    method: "POST",
    responseType: "text",
  })
}

export function useResourceLazy() {
  const auth = useAuth()
  return useCallback((id: number) => {
    return fetchApi(`${API_URL}/admin/resources/${id}`, {
      method: "GET",
      responseType: "json",
      headers: {
        Authorization: `Bearer ${auth.tokens?.access}`,
      },
    }) as Promise<Resource>
  }, [])
}

export function useResourcesLazy(params: Record<string, string | number | Array<number>> = {}) {
  const auth = useAuth()
  return useCallback((paramsOverride: Record<string, string | number | Array<number>> = {}) => {
    const combinedParams = { ...params, ...paramsOverride }
    const qs = normalizeParams(combinedParams).toString()
    return fetchApi(`${API_URL}/admin/resources?${qs}`, {
      method: "GET",
      responseType: "json",
      headers: {
        Authorization: `Bearer ${auth.tokens?.access}`,
      },
    }) as Promise<{ results: Resource[] }>
  }, [])
}

export function useMediaListResource(id: number | undefined, params: Record<string, string> = {}) {
  return usePlainList<ResourceMedia>(`${API_URL}/admin/resources/${id}/resource-media`, {
    query: params,
  })
}

export function useCreateMediaResource(id: number | undefined) {
  return useCreate<ResourceMedia>(`${API_URL}/admin/resources/${id}/create-resource-media`, {
    encode: "formdata",
  })
}

export function useDeleteMediaResource() {
  const auth = useAuth()
  return useCallback((id: number, idMedia: number) => {
    return fetchApi(`${API_URL}/admin/resources/${id}/delete-resource-media/${idMedia}/`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${auth.tokens?.access}`,
        "Content-Type": "application/json",
      },
    }) as Promise<void>
  }, [])
  //  return useDelete(`${API_URL}/admin/records/${id}/delete-record-media/${idMedia}`)
}

export function usePartialUpdateMediaResource() {
  const auth = useAuth()
  return useCallback((id: number, idMedia: number, values: Partial<ResourceMedia>) => {
    return fetchApi(`${API_URL}/admin/resources/${id}/update-resource-media/${idMedia}/`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${auth.tokens?.access}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(values),
    }) as Promise<ResourceMedia>
  }, [])
}
